'use client'

import { addressFormatter, type ITravelPointQuote, useLocationApi } from '@repo/sdk'
import { cn } from '@repo/ui/lib'
import { useSuspenseQuery } from '@tanstack/react-query'
import { Suspense, useRef } from 'react'

import {
  Skeleton,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../index.ts'

interface ILocationProps {
  data: ITravelPointQuote
  location: 'destination' | 'origin'
  popup?: boolean
  status: 'canceled' | string
}

export const LocationCell = (props: ILocationProps) => {
  'use memo'

  return (
    <Suspense
      fallback={
        <div className="flex flex-col items-start gap-1">
          <Skeleton className="w-full" />
          <Skeleton className="w-full" />
        </div>
      }
    >
      <LocationCellLoader {...props} />
    </Suspense>
  )
}

const LocationCellLoader = ({ data, popup, status }: ILocationProps) => {
  'use memo'

  const { getLatLng } = useLocationApi()

  const { data: addressData } = useSuspenseQuery({
    async queryFn() {
      try {
        return await getLatLng(data?.place.place_id)
      } catch (error) {
        return null
      }
    },
    queryKey: ['quotesLocation', data.id],
  })

  const formattedAddress = addressFormatter(addressData?.address_components)

  const addressBookName = data.place?.address_book?.name
  const locationName =
    formattedAddress?.city ?? formattedAddress?.neighborhood ?? data.place?.city ?? ''

  const placeName = addressBookName
  const address = locationName
  const postcode = formattedAddress?.postcode

  const textRef = useRef<HTMLDivElement>(null)

  const CellBody = () => {
    'use memo'
    return (
      <div className="flex flex-col items-start gap-1">
        {placeName && (
          <p
            className={cn(
              'w-full truncate text-left text-xs text-[#637381] dark:text-[#d4d9de]',
              status === 'canceled' && 'text-[#C3C3C3]',
            )}
            ref={textRef}
          >
            {placeName}
          </p>
        )}
        <div
          className={cn(
            'flex w-full items-center gap-1 truncate font-medium text-[#212B36] dark:text-[#b6c4d2]',
            status === 'canceled' && 'text-[#C3C3C3]',
          )}
        >
          <p className="truncate text-ellipsis whitespace-nowrap text-left text-sm">
            {address}, {formattedAddress.countryCode}
          </p>
        </div>
        {postcode && (
          <p
            className={cn(
              'w-full truncate whitespace-nowrap text-left text-xs text-[#637381] dark:text-[#d4d9de]',
              status === 'canceled' && 'text-[#C3C3C3]',
            )}
          >
            {postcode}
          </p>
        )}
      </div>
    )
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger aria-haspopup={popup} className={'max-w-full'}>
            <CellBody />
          </TooltipTrigger>
          <TooltipContent className={cn('!text-white')} side={'bottom'}>
            <TooltipArrow />
            <div className="text-black">{data.place.place_name}</div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </>
  )
}
